import React, { useMemo, useEffect, useState } from 'react';
import _ from 'underscore';
import { PropTypes } from 'prop-types';
import { MessageBar, MessageBarTitle, MessageBarBody } from '@fluentui/react-message-bar';
import { StackedBarChart } from '@fluentui/react-charting';
import { Stack } from '@fluentui/react';
import { Divider, makeStyles, tokens } from '@fluentui/react-components';
import { isXandrCampaign, CampaignSubTypeMapping } from '@bingads-webui-campaign-react/constants';
import { unitValueMap } from '@bingads-webui-campaign/reach-estimate-control';

import { useIMATargetingEstimationCaller } from './use-targeting-estimation-caller';
import { formatBigDecimal } from '../utils';

const useStackStyles = makeStyles({
  estimationBody: {
    marginBottom: tokens.spacingVerticalMNudge,
  },
  firstDivider: {
    marginBottom: tokens.spacingVerticalMNudge,
  },
});

const estimation = (data) => {
  const estimationAudienceUnit = _.get(data, ['EstAudienceSize', 'Unit']) || 'None';
  return unitValueMap[estimationAudienceUnit].value * (_.get(data, ['EstAudienceSize', 'Value']) || 0);
};

export const IMAAudienceEstimation = ({
  targetedInterests = [], excludedInterests = [], i18n, autoTargeting, campaignBiddingStrategy, campaignSubType,
}) => {
  const stackStyles = useStackStyles();
  const [errorMessage, setErrorMessage] = useState('');
  const otherProps = useMemo(() => ({
    AutoTargeting: autoTargeting,
    CampaignBiddingStrategy: campaignBiddingStrategy,
    CampaignSubType: CampaignSubTypeMapping[campaignSubType],
  }), [autoTargeting, campaignBiddingStrategy, campaignSubType]);

  const includesAudiences = useMemo(() => (targetedInterests ? targetedInterests.map(({ Criterion }) => Criterion.AudienceId) : []), [targetedInterests]);
  const excludesAudiences = useMemo(() => (excludedInterests ? excludedInterests.map(({ Criterion }) => Criterion.AudienceId) : []), [excludedInterests]);

  const IMATotal = useMemo(() => ({ Audience: { Includes: [], Excludes: [] }, ...otherProps }), [otherProps]);
  const IMAIncluded = useMemo(() => ({ Audience: { Includes: includesAudiences, Excludes: [] }, ...otherProps }), [includesAudiences, otherProps]);
  const IMAAll = useMemo(() => ({ Audience: { Includes: includesAudiences, Excludes: excludesAudiences }, ...otherProps }), [includesAudiences, excludesAudiences, otherProps]);

  const [
    isAudienceEstimationLoading,
    totalPotentialData,
    includedOnlyData,
    allTargetedData,
    isImpressionTooSpecific,
  ] = useIMATargetingEstimationCaller(IMATotal, IMAIncluded, IMAAll);

  let selectedData;
  let excludedData;
  let potentialdata;

  const includedAudiencesOnly = useMemo(() => targetedInterests.length > 0 && !excludedInterests.length, [targetedInterests, excludedInterests]);
  const excludedAudiencesOnly = useMemo(() => !targetedInterests.length && excludedInterests.length > 0, [targetedInterests, excludedInterests]);

  const estimatedIncludedOnlyData = estimation(includedOnlyData) || 0;
  const estimatedAllTargetedData = estimation(allTargetedData) || 0;
  const estimatedPotentialData = estimation(totalPotentialData) || 0;

  if (targetedInterests.length === 0 && excludedInterests.length === 0) {
    selectedData = 0;
    excludedData = 0;
    potentialdata = estimatedPotentialData;
  } else if (includedAudiencesOnly) {
    selectedData = estimatedIncludedOnlyData;
    excludedData = 0;
    potentialdata = estimatedPotentialData - estimatedIncludedOnlyData;
  } else if (excludedAudiencesOnly) {
    selectedData = estimatedAllTargetedData;
    excludedData = estimatedPotentialData - estimatedAllTargetedData;
    potentialdata = 0;
  } else {
    selectedData = estimatedAllTargetedData;
    excludedData = estimatedIncludedOnlyData - estimatedAllTargetedData;
    potentialdata = estimatedPotentialData - estimatedIncludedOnlyData;
  }

  const points = [
    {
      legend: i18n.getString(_TL_('Selected audience size')), data: selectedData, color: '#637CEF', xAxisCalloutData: i18n.getString(_TL_('Selected audience size')), yAxisCalloutData: formatBigDecimal(selectedData, i18n),
    },
    {
      legend: i18n.getString(_TL_('Excluded')), data: excludedData, color: '#F1511B', xAxisCalloutData: i18n.getString(_TL_('Excluded')), yAxisCalloutData: formatBigDecimal(excludedData, i18n),
    },
    {
      legend: i18n.getString(_TL_('Potential available reach')), data: potentialdata, color: '#D9D9D9', xAxisCalloutData: i18n.getString(_TL_('Potential available reach')), yAxisCalloutData: formatBigDecimal(potentialdata, i18n),
    },
  ];

  const data = {
    chartTitle: i18n.getString(_TL_('Audience size')),
    chartData: points,
  };

  useEffect(() => {
    if (!isAudienceEstimationLoading) {
      if (isImpressionTooSpecific) {
        setErrorMessage(i18n.getString(_TL_('Very narrow.')));
      } else {
        setErrorMessage('');
      }
    }
  }, [isAudienceEstimationLoading, i18n, isImpressionTooSpecific]);

  if (isXandrCampaign(campaignSubType)) {
    // ad insight estimation API does not support Xandr campaigns
    return null;
  }

  return (
    <div className={stackStyles.estimationBody}>
      <Divider className={stackStyles.firstDivider} />
      <Stack tokens={{ childrenGap: 10 }}>
        <StackedBarChart
          data={data}
          ignoreFixStyle={false}
        />
        {errorMessage &&
          <MessageBar
            key="warning"
            intent="warning"
          >
            <MessageBarBody>
              <MessageBarTitle>Warning</MessageBarTitle>
              {errorMessage}
            </MessageBarBody>
          </MessageBar>
        }
        <Divider />
      </Stack>
    </div>
  );
};

IMAAudienceEstimation.propTypes = {
  targetedInterests: PropTypes.arrayOf(PropTypes.shape({})),
  excludedInterests: PropTypes.arrayOf(PropTypes.shape({})),
  i18n: PropTypes.shape({ getString: PropTypes.func }).isRequired,
  autoTargeting: PropTypes.string.isRequired,
  campaignBiddingStrategy: PropTypes.string.isRequired,
  campaignSubType: PropTypes.string.isRequired,
};

IMAAudienceEstimation.defaultProps = {
  targetedInterests: [],
  excludedInterests: [],
};
